"use client";

import React from "react";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactDetails = [{
  id: "phone",
  title: "Call Us Directly",
  value: "0466 880 255",
  href: "tel:0466880255",
  icon: Phone,
  description: "Available for urgent inquiries and quotes.",
  colorClass: "text-primary",
  bgClass: "bg-primary/10"
}, {
  id: "email",
  title: "Send an Email",
  value: "sales@powershine.com.au",
  href: "mailto:info@powershine.com.au",
  icon: Mail,
  description: "We aim to respond within 24 hours.",
  colorClass: "text-chart-2",
  bgClass: "bg-chart-2/10"
}];
const locationDetails = [{
  id: "address",
  title: "Service Area",
  value: "Central Coast, NSW",
  icon: MapPin
}, {
  id: "hours",
  title: "Business Hours",
  value: "By appointment only",
  icon: Clock
}];
export default function ContactInformation() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: mapRef,
    isVisible: isMapVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact-information" className="relative w-full bg-background py-24 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-2xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
            Get in Touch with <span className="text-primary">Power Shine</span>
          </h2>
          <p className="text-lg text-muted-foreground">Ready to maximise your solar efficiency? Contact us today for a free quote or to schedule your next cleaning service.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {}
          <div ref={cardsRef} className={`lg:col-span-5 flex flex-col gap-6 transition-all duration-700 delay-100 ease-out ${areCardsVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            {contactDetails.map((contact, i) => <Link key={contact.id} to={contact.href} className="group block" data-index={i}>
                <Card className="h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent to-muted/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <CardContent className="p-8 flex items-start gap-6 relative z-10">
                    <div className={`shrink-0 p-4 rounded-2xl ${contact.bgClass} ${contact.colorClass} transition-transform duration-300 group-hover:scale-110`}>
                      <contact.icon className="w-8 h-8" />
                    </div>
                    <div className="flex-col flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                          {contact.title}
                        </h3>
                        <ArrowUpRight className="w-5 h-5 text-muted-foreground opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:-translate-y-0 transition-all duration-300" />
                      </div>
                      
                      <p className="text-sm text-muted-foreground">
                        {contact.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>)}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              {locationDetails.map((detail, i) => <Card key={detail.id} className="border-border/50 bg-card/30 backdrop-blur-sm" data-index={i}>
                  <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                    <div className="p-3 rounded-full bg-secondary text-secondary-foreground">
                      <detail.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">{detail.title}</h4>
                      <p className="text-base font-semibold text-foreground">{detail.value}</p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>

          {}
          <div ref={mapRef} className={`lg:col-span-7 h-[500px] lg:h-full min-h-[500px] rounded-3xl overflow-hidden relative shadow-2xl border border-border/50 transition-all duration-700 delay-200 ease-out ${isMapVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {}
            <div className="absolute inset-0 bg-muted/30">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1585875/img/brisbane_service_area_map.png" alt="Map of Brisbane service area" className="w-full h-full object-cover opacity-80 mix-blend-luminosity hover:mix-blend-normal transition-all duration-700" />
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            </div>

            {}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
              <div className="relative">
                {}
                <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-20 scale-150" />
                <div className="absolute inset-0 bg-primary rounded-full animate-pulse opacity-40 scale-110" />
                
                {}
                <div className="relative bg-background p-3 rounded-full shadow-xl border-2 border-primary z-10">
                  <MapPin className="w-8 h-8 text-primary" />
                </div>
              </div>
              
              {}
              <div className="mt-4 bg-background/95 backdrop-blur-md px-6 py-3 rounded-2xl shadow-lg border border-border/50 text-center transform transition-transform hover:scale-105">
                <p className="font-bold text-foreground text-lg">Power Shine</p>
                <p className="text-sm text-muted-foreground font-medium">Central Coast NSW</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}